import axios from 'axios'

import type { Settings } from '../../shared/types/user'
import {
	clearPendingSettingsCache,
	getCurrentSettingsCache,
	getPendingSettingsCache,
	setPendingSettingsCache,
	setSettingsCache,
} from '../cache/settings'
import { apiUrl, IS_NATIVE_APP } from '../config/environment'
import { authenticatedFetch } from './authenticatedFetch'
import { getUser, hasAuthenticatedUser, updateCachedUserSettings } from './user'

const SETTINGS_FLUSH_DEBOUNCE_MS = 1000
const SETTINGS_FLUSH_RETRY_MS = 10_000
const OVERLAY_CLOSE_EVENT = 'tunetoss:overlay-close'

let settingsFlushTimer: number | null = null
let pendingFlush: Promise<void> | null = null
let settingsSyncLifecycleConfigured = false

function cloneSettings(settings: Settings): Settings {
	return { ...settings }
}

function serializeSettings(settings: Settings): string {
	return JSON.stringify(settings)
}

function applySettingsLocally(settings: Settings): Settings {
	const settingsSnapshot = cloneSettings(settings)
	setSettingsCache(settingsSnapshot)
	updateCachedUserSettings(settingsSnapshot)
	return settingsSnapshot
}

async function saveSettingsToServer(settings: Settings): Promise<Settings> {
	const res = await axios.post('/api/settings', { settings })
	return (res.data?.data as Settings | undefined) ?? settings
}

function clearFlushTimer(): void {
	if (!settingsFlushTimer) return
	window.clearTimeout(settingsFlushTimer)
	settingsFlushTimer = null
}

function schedulePendingSettingsFlush(
	delayMs = SETTINGS_FLUSH_DEBOUNCE_MS,
): void {
	clearFlushTimer()
	settingsFlushTimer = window.setTimeout(() => {
		settingsFlushTimer = null
		void flushPendingSettings()
	}, delayMs)
}

function schedulePendingSettingsRetry(): void {
	if (settingsFlushTimer) return
	schedulePendingSettingsFlush(SETTINGS_FLUSH_RETRY_MS)
}

function clearPendingSettingsIfConfirmed(sentSnapshot: string): void {
	const pendingSettings = getPendingSettingsCache()
	if (
		pendingSettings &&
		serializeSettings(pendingSettings) === sentSnapshot
	) {
		clearPendingSettingsCache()
	}
}

function scheduleNextFlushIfSettingsChanged(sentSnapshot: string): void {
	const latestPending = getPendingSettingsCache()
	if (!latestPending) return
	if (serializeSettings(latestPending) !== sentSnapshot) {
		schedulePendingSettingsFlush(0)
	}
}

/**
 * Returns cached settings when available, otherwise resolves them via the current user.
 */
export async function getSettings(): Promise<Settings | null> {
	try {
		const cached = getCurrentSettingsCache()
		if (cached) return applySettingsLocally(cached)

		const user = await getUser()
		return user?.settings ? cloneSettings(user.settings) : null
	} catch (error) {
		console.error('Failed to get settings:', error)
		return null
	}
}

/**
 * Persists settings on the server and refreshes the local cache.
 */
export async function updateSettings(settings: Settings): Promise<void> {
	const settingsSnapshot = applySettingsLocally(settings)
	const sentSnapshot = serializeSettings(settingsSnapshot)

	try {
		const updatedSettings = await saveSettingsToServer(settingsSnapshot)
		applySettingsLocally(updatedSettings)
		clearPendingSettingsIfConfirmed(sentSnapshot)
	} catch (error) {
		console.error('Failed to update settings:', error)
	}
}

/**
 * Applies settings locally and queues a debounced server save.
 */
export function queueSettingsUpdate(settings: Settings): void {
	const settingsSnapshot = applySettingsLocally(settings)
	setPendingSettingsCache(settingsSnapshot)
	schedulePendingSettingsFlush()
}

/**
 * Flushes the latest pending settings snapshot to the server.
 */
export async function flushPendingSettings(): Promise<void> {
	if (pendingFlush) return pendingFlush

	clearFlushTimer()
	const pendingSettings = getPendingSettingsCache()
	if (!pendingSettings || !hasAuthenticatedUser()) return

	const settingsSnapshot = cloneSettings(pendingSettings)
	const sentSnapshot = serializeSettings(settingsSnapshot)

	pendingFlush = (async () => {
		try {
			const updatedSettings = await saveSettingsToServer(settingsSnapshot)
			const latestPending = getPendingSettingsCache()
			if (
				latestPending &&
				serializeSettings(latestPending) === sentSnapshot
			) {
				clearPendingSettingsCache()
				applySettingsLocally(updatedSettings)
				return
			}

			scheduleNextFlushIfSettingsChanged(sentSnapshot)
		} catch (error) {
			console.error('Failed to flush pending settings:', error)
			schedulePendingSettingsRetry()
		} finally {
			pendingFlush = null
		}
	})()

	return pendingFlush
}

/**
 * Registers lifecycle hooks that retry or best-effort flush pending settings.
 */
export function setupSettingsSyncLifecycle(): void {
	if (settingsSyncLifecycleConfigured) return
	settingsSyncLifecycleConfigured = true

	window.addEventListener('online', () => {
		schedulePendingSettingsFlush(0)
	})
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') {
			void flushPendingSettings()
		}
	})
	window.addEventListener('pagehide', sendPendingSettingsBeacon)
	document.addEventListener(OVERLAY_CLOSE_EVENT, () => {
		void flushPendingSettings()
	})
}

function sendPendingSettingsBeacon(): void {
	const pendingSettings = getPendingSettingsCache()
	if (!pendingSettings) return

	const body = JSON.stringify({ settings: pendingSettings })
	if (!IS_NATIVE_APP && navigator.sendBeacon) {
		const blob = new Blob([body], { type: 'application/json' })
		navigator.sendBeacon(apiUrl('/api/settings'), blob)
		return
	}

	void authenticatedFetch(apiUrl('/api/settings'), {
		body,
		headers: {
			'Content-Type': 'application/json',
		},
		keepalive: true,
		method: 'POST',
	}).catch((error: unknown) => {
		console.error('Failed to send pending settings beacon fallback:', error)
	})
}

/**
 * Deletes all saved listen-history data for the current user.
 */
export async function resetHistoryData(): Promise<void> {
	await axios.delete('/api/settings/history')
}
